import { useMemo } from 'react'
import { useLang } from '../i18n'
import type { WorkflowNode, NodeStatut } from '../lib/api'
import { computeDepths } from '../lib/workflow'

interface WorkflowDagProps {
  nodes: WorkflowNode[]
  onSelectNode: (nodeId: string) => void
  selectedNodeId: string | null
}

const STATUT_COLOR: Record<NodeStatut, string> = {
  valide:    'node-valide',
  en_cours:  'node-en-cours',
  a_revoir:  'node-a-revoir',
  a_faire:   'node-a-faire',
  bloque:    'node-bloque',
}

const STATUT_ICONS: Record<NodeStatut, string> = {
  valide:    '✓',
  en_cours:  '◐',
  a_revoir:  '↺',
  a_faire:   '○',
  bloque:    '🔒',
}

export function WorkflowDag({ nodes, onSelectNode, selectedNodeId }: WorkflowDagProps) {
  const { t } = useLang()

  const depths = useMemo(() => computeDepths(nodes), [nodes])

  // Group nodes by depth column (left → right)
  const columns = useMemo(() => {
    const cols = new Map<number, WorkflowNode[]>()
    for (const n of nodes) {
      const d = depths.get(n.nodeId) ?? 0
      if (!cols.has(d)) cols.set(d, [])
      cols.get(d)!.push(n)
    }
    return [...cols.entries()].sort((a, b) => a[0] - b[0])
  }, [nodes, depths])

  // Build SVG connector paths between nodes
  // We render nodes in a grid; connectors link output to input via SVG overlay
  const NODE_W = 174
  const NODE_H = 66
  const COL_GAP = 52
  const ROW_GAP = 12
  const PAD = 16

  // Compute positions: { nodeId → { cx, cy } } (center of card)
  const positions = useMemo(() => {
    const pos = new Map<string, { x: number; y: number }>()
    let x = PAD
    for (const [, col] of columns) {
      let y = PAD
      for (const n of col) {
        pos.set(n.nodeId, { x: x + NODE_W / 2, y: y + NODE_H / 2 })
        y += NODE_H + ROW_GAP
      }
      x += NODE_W + COL_GAP
    }
    return pos
  }, [columns])

  const totalW = columns.length * (NODE_W + COL_GAP) - COL_GAP + PAD * 2
  const maxRows = Math.max(...columns.map(([, c]) => c.length), 1)
  const totalH = maxRows * (NODE_H + ROW_GAP) - ROW_GAP + PAD * 2

  return (
    <div className="dag-wrap" style={{ position: 'relative', overflowX: 'auto' }}>
      {/* SVG connectors underneath cards */}
      <svg
        width={totalW}
        height={totalH}
        style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', zIndex: 0 }}
        aria-hidden="true"
      >
        {nodes.flatMap((n) =>
          n.dependsOn.map((depId) => {
            const from = positions.get(depId)
            const to = positions.get(n.nodeId)
            if (!from || !to) return null
            const x1 = from.x + NODE_W / 2 - PAD
            const y1 = from.y
            const x2 = to.x - NODE_W / 2 + PAD
            const y2 = to.y
            const mx = (x1 + x2) / 2
            const dep = nodes.find((d) => d.nodeId === depId)
            const isOnCritical = n.surCheminCritique && (dep?.surCheminCritique ?? false)
            return (
              <path
                key={`${depId}->${n.nodeId}`}
                d={`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`}
                fill="none"
                stroke={isOnCritical ? 'var(--amber)' : 'var(--line-2)'}
                strokeWidth={isOnCritical ? 2 : 1.5}
                strokeDasharray={n.statut === 'bloque' ? '4 3' : undefined}
                opacity={n.enabled ? 1 : 0.35}
              />
            )
          }),
        )}
      </svg>

      {/* Node cards */}
      <div style={{ position: 'relative', width: totalW, height: totalH, zIndex: 1 }}>
        {nodes.map((n) => {
          const pos = positions.get(n.nodeId)
          if (!pos) return null
          const x = pos.x - NODE_W / 2
          const y = pos.y - NODE_H / 2
          const isSelected = n.nodeId === selectedNodeId
          return (
            <button
              key={n.nodeId}
              className={`dag-node ${STATUT_COLOR[n.statut]}${isSelected ? ' dag-node-sel' : ''}${!n.enabled ? ' dag-node-disabled' : ''}`}
              style={{ left: x, top: y, width: NODE_W, height: NODE_H }}
              onClick={() => onSelectNode(n.nodeId)}
              aria-pressed={isSelected}
              aria-label={`${n.section} — ${t(`wf_statut_${n.statut}` as never)}`}
              title={n.section}
            >
              <span className="dag-icon">{STATUT_ICONS[n.statut]}</span>
              <span className="dag-label">{n.section}</span>
              {n.surCheminCritique && <span className="dag-crit" aria-hidden="true" />}
            </button>
          )
        })}
      </div>
    </div>
  )
}
